/**
 * 🛠️ KilometrageAdminBlock — v13.63 Phase 5 (2026-02-28)
 *
 * Bloc admin qui affiche le kilométrage payable d'un employé pour une
 * journée donnée, avec la possibilité d'AJUSTER ou de REFUSER le montant
 * (motif obligatoire, traçabilité complète).
 *
 * Utilisé dans « Validation des heures » à côté des punchs du jour.
 * Guardé par la clé RBAC `heures_kilometrage_ajuster` (vérifiée par le
 * conteneur parent via canDo).
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Car, Loader2, Check, X, RotateCcw, MapPin, ChevronDown, ChevronUp, AlertTriangle,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

function fmt2(v) { return (v ?? 0).toFixed(2); }
function fmtMoney(v) { return `${fmt2(v)} $`; }

const SEG_TYPE_LABELS = { aller: 'Aller', retour: 'Retour', inter: 'Entre chantiers' };
const SEG_TYPE_COLORS = {
  aller: 'bg-blue-100 text-blue-700 border-blue-300',
  retour: 'bg-blue-100 text-blue-700 border-blue-300',
  inter: 'bg-emerald-100 text-emerald-700 border-emerald-300',
};

function AjustementDialog({ open, onOpenChange, currentKm, currentMontant, taux, onSubmit }) {
  const [mode, setMode] = useState('corrige');
  const [kmAjust, setKmAjust] = useState('');
  const [motif, setMotif] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setMode('corrige');
      setKmAjust(String(currentKm || 0));
      setMotif('');
    }
  }, [open, currentKm]);

  const montantEstime = mode === 'refuse' ? 0 : Math.round((parseFloat(kmAjust) || 0) * (taux || 0) * 100) / 100;

  const handleSubmit = async () => {
    if (motif.trim().length < 3) {
      toast.error('Motif obligatoire (min 3 caractères).');
      return;
    }
    if (mode === 'corrige' && (kmAjust === '' || parseFloat(kmAjust) < 0)) {
      toast.error('Km ajustés doit être ≥ 0.');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        statut: mode,
        km_payables_ajustes: mode === 'refuse' ? 0 : parseFloat(kmAjust),
        motif: motif.trim(),
      });
      onOpenChange(false);
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dialog-km-ajustement">
        <DialogHeader>
          <DialogTitle className="text-base">Ajuster le kilométrage</DialogTitle>
          <DialogDescription className="text-xs">
            Valeur calculée : <span className="font-semibold">{fmt2(currentKm)} km · {fmtMoney(currentMontant)}</span>.
            Toute modification est tracée (motif, auteur, date, valeur avant/après).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMode('corrige')}
              className={`p-2 rounded border text-sm font-medium ${mode === 'corrige' ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
              data-testid="btn-mode-corriger"
            >
              Corriger le montant
            </button>
            <button
              type="button"
              onClick={() => setMode('refuse')}
              className={`p-2 rounded border text-sm font-medium ${mode === 'refuse' ? 'border-red-500 bg-red-50 text-red-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
              data-testid="btn-mode-refuser"
            >
              Refuser (0 $)
            </button>
          </div>

          {mode === 'corrige' && (
            <div>
              <Label htmlFor="km-ajust" className="text-xs">Km payables ajustés</Label>
              <Input
                id="km-ajust"
                type="number"
                step="0.01"
                min="0"
                value={kmAjust}
                onChange={(e) => setKmAjust(e.target.value)}
                data-testid="input-km-ajust"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                → Montant estimé : <span className="font-semibold">{fmtMoney(montantEstime)}</span> (taux {fmtMoney(taux)}/km)
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="km-motif" className="text-xs">
              Motif (obligatoire, min 3 caractères)
              <span className="text-red-500 ml-1">*</span>
            </Label>
            <Textarea
              id="km-motif"
              rows={2}
              maxLength={500}
              placeholder="Ex. : Covoiturage — pas conduit / Départ chantier voisin / Cédule papier vérifiée"
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              data-testid="input-km-motif"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={submitting}>
            Annuler
          </Button>
          <Button
            size="sm"
            className={mode === 'refuse' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}
            onClick={handleSubmit}
            disabled={submitting || motif.trim().length < 3}
            data-testid="btn-confirm-km-ajustement"
          >
            {submitting && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
            {mode === 'refuse' ? 'Confirmer le refus' : 'Enregistrer la correction'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function KilometrageAdminBlock({ employeeId, dateIso, token, canAjuster = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const authHdr = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

  const fetchData = useCallback(async () => {
    if (!employeeId || !dateIso) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/kilometrage/employe/${employeeId}/${dateIso}`, authHdr);
      setData(r.data);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  }, [employeeId, dateIso, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmitAjustement = async (payload) => {
    try {
      await axios.post(
        `${API_URL}/api/kilometrage/employe/${employeeId}/${dateIso}/ajustement`,
        payload, authHdr,
      );
      toast.success(payload.statut === 'refuse' ? 'Kilométrage refusé.' : 'Correction enregistrée.');
      await fetchData();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Impossible d'enregistrer l'ajustement.";
      toast.error(typeof msg === 'string' ? msg : 'Erreur de validation.');
      throw e;
    }
  };

  const handleRetirer = async () => {
    const motif = window.prompt('Motif du retrait de l\'ajustement (min 3 caractères) :');
    if (!motif || motif.trim().length < 3) return;
    try {
      await axios.delete(`${API_URL}/api/kilometrage/employe/${employeeId}/${dateIso}/ajustement`, {
        ...authHdr,
        data: { motif: motif.trim() },
      });
      toast.success('Ajustement retiré.');
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur retrait ajustement.');
    }
  };

  if (loading && !data) {
    return (
      <div className="text-xs text-slate-400 flex items-center gap-1.5 py-1" data-testid="km-admin-loading">
        <Loader2 className="w-3 h-3 animate-spin" /> Kilométrage…
      </div>
    );
  }

  if (!data) return null;

  // ─── Cas rejeté : pas de trajet ──────────────────────────────────
  if (data.status === 'adresse_domicile_manquante') {
    return (
      <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-center gap-1.5"
           data-testid="km-admin-adresse-manquante">
        <AlertTriangle className="w-3 h-3" />
        Domicile manquant — kilométrage indisponible
      </div>
    );
  }
  if (data.status === 'aucun_punch_du_jour') return null;

  const res = data.resultat || {};
  const segAuto = res.segments_auto || [];
  const segManuel = res.segments_manuels || [];
  const ajust = data.ajustement;
  const kmFinal = data.km_payables_final ?? 0;
  const mntFinal = data.montant_dollars_final ?? 0;
  const kmAuto = res.total_payable_km ?? 0;
  const mntAuto = res.total_montant_dollars ?? 0;
  const taux = res.taux_dollars_par_km_utilise ?? 0;
  const franchise = res.total_franchise_km ?? 0;

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-2 space-y-2"
         data-testid={`km-admin-block-${dateIso}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Car className="w-3.5 h-3.5 text-emerald-700" />
          <span className="text-xs font-semibold uppercase tracking-wide text-emerald-800">
            Kilométrage
          </span>
          {ajust && (
            <Badge className={`text-[10px] ${ajust.statut === 'refuse' ? 'bg-red-100 text-red-700 border-red-300' : 'bg-amber-100 text-amber-700 border-amber-300'}`}
                   data-testid="km-admin-ajust-badge">
              {ajust.statut === 'refuse' ? 'Refusé par admin' : 'Ajusté par admin'}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs">
          <div className="text-right">
            <p className="text-[10px] text-slate-500 uppercase">À payer</p>
            <p className={`font-bold ${ajust ? 'text-amber-700' : 'text-emerald-700'}`}
               data-testid={`km-admin-final-${dateIso}`}>
              {fmt2(kmFinal)} km · {fmtMoney(mntFinal)}
            </p>
          </div>
          {canAjuster && (
            <div className="flex gap-1">
              <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                      onClick={() => setShowDialog(true)} data-testid={`btn-open-km-ajust-${dateIso}`}>
                Ajuster
              </Button>
              {ajust && (
                <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] text-slate-600"
                        onClick={handleRetirer} title="Retirer l'ajustement" data-testid={`btn-remove-km-ajust-${dateIso}`}>
                  <RotateCcw className="w-3 h-3" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Grille compact : distance / franchise / taux / auto vs ajust */}
      <div className="grid grid-cols-4 gap-1 text-[11px] pt-1 border-t border-emerald-100">
        <div>
          <p className="text-slate-500">Distance</p>
          <p className="font-semibold text-slate-800">{fmt2(res.total_distance_km)} km</p>
        </div>
        <div>
          <p className="text-slate-500">Franchise</p>
          <p className="font-semibold text-slate-800">{fmt2(franchise)} km</p>
        </div>
        <div>
          <p className="text-slate-500">Auto</p>
          <p className={`font-semibold ${ajust ? 'text-slate-400 line-through' : 'text-emerald-700'}`}>
            {fmt2(kmAuto)} km · {fmtMoney(mntAuto)}
          </p>
        </div>
        <div>
          <p className="text-slate-500">Taux</p>
          <p className="font-semibold text-slate-800">{fmtMoney(taux)}/km</p>
        </div>
      </div>

      {ajust && (
        <div className="text-[11px] bg-white/80 rounded px-2 py-1 border border-amber-200"
             data-testid={`km-admin-ajust-detail-${dateIso}`}>
          <p className="font-semibold text-amber-800">Motif : {ajust.motif}</p>
          <p className="text-slate-500">
            Avant : {fmt2(ajust.km_payables_avant)} km · {fmtMoney(ajust.montant_dollars_avant)}
            {' • '}Après : {fmt2(ajust.km_payables_ajustes)} km · {fmtMoney(ajust.montant_dollars_ajuste)}
          </p>
          <p className="text-slate-400 text-[10px]">
            Par {ajust.auteur_nom || ajust.auteur_role} — {new Date(ajust.date).toLocaleString('fr-CA')}
          </p>
        </div>
      )}

      {/* Détail segments (expandable) */}
      {(segAuto.length > 0 || segManuel.length > 0) && (
        <div>
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="text-[11px] text-emerald-700 hover:text-emerald-800 font-medium flex items-center gap-0.5"
            data-testid={`btn-km-admin-expand-${dateIso}`}
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {expanded ? 'Masquer' : 'Voir'} le détail ({segAuto.length + segManuel.length} segments)
          </button>
          {expanded && (
            <div className="mt-1 space-y-1">
              {segAuto.map((s, i) => (
                <div key={`a-${i}`} className="flex items-start justify-between text-[11px] bg-white/70 rounded px-2 py-1">
                  <div className="flex items-start gap-1 min-w-0 flex-1">
                    <Badge className={`text-[9px] mt-0.5 ${SEG_TYPE_COLORS[s.type]}`}>{SEG_TYPE_LABELS[s.type]}</Badge>
                    <div className="min-w-0">
                      <p className="text-slate-700 truncate flex items-center gap-1">
                        <MapPin className="w-2.5 h-2.5 text-slate-400" />{s.origine} → {s.destination}
                      </p>
                    </div>
                  </div>
                  <div className="text-right ml-2 shrink-0">
                    <p className="text-slate-500">{s.distance_km != null ? `${fmt2(s.distance_km)} km` : '—'}</p>
                    <p className="font-semibold text-emerald-700">{fmt2(s.payable_km)} km · {fmtMoney(s.montant_dollars)}</p>
                  </div>
                </div>
              ))}
              {segManuel.length > 0 && segManuel.map((s) => (
                <div key={s.id} className="flex items-start justify-between text-[11px] bg-violet-50 rounded px-2 py-1 border border-violet-200">
                  <div className="flex items-start gap-1 min-w-0 flex-1">
                    <Badge className="text-[9px] mt-0.5 bg-violet-100 text-violet-700 border-violet-300">Ajouté employé</Badge>
                    <p className="text-slate-700 truncate">{s.origine} → {s.destination}</p>
                  </div>
                  <p className="font-semibold text-violet-700 shrink-0">{fmt2(s.payable_km)} km · {fmtMoney(s.montant_dollars)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <AjustementDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        currentKm={kmAuto}
        currentMontant={mntAuto}
        taux={taux}
        onSubmit={handleSubmitAjustement}
      />
    </div>
  );
}
